import React, { useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { entidadesApi } from '../../services/api';
import { AlertTriangle, X, Loader2, TrendingDown, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function AnalisisInactividad() {
  const [abierto, setAbierto] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [marcando, setMarcando] = useState(new Set());
  const [marcandoTodas, setMarcandoTodas] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await entidadesApi.analisisInactividad();
      setItems(res.items || []);
    } catch (e) {
      toast.error('Error cargando análisis: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const abrir = () => { setAbierto(true); cargar(); };
  const cerrar = () => { setAbierto(false); setItems([]); };

  const marcarInactivo = async (id) => {
    setMarcando(prev => new Set([...prev, id]));
    try {
      await entidadesApi.marcarInactivo(id);
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success('Entidad marcada como inactiva');
    } catch (e) {
      toast.error('Error: ' + e.message);
    } finally {
      setMarcando(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const marcarTodas = async () => {
    if (!window.confirm(`¿Marcar ${items.length} entidades como inactivas?`)) return;
    setMarcandoTodas(true);
    let ok = 0;
    for (const item of items) {
      try { await entidadesApi.marcarInactivo(item.id); ok++; } catch { /* continúa */ }
    }
    toast.success(`${ok} entidades marcadas como inactivas`);
    setItems([]);
    setMarcandoTodas(false);
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={abrir}
        className="border-amber-200 text-amber-700 hover:bg-amber-50"
      >
        <TrendingDown className="w-4 h-4 mr-1.5" />
        Análisis de inactividad
      </Button>

      {abierto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
            {/* Cabecera */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Análisis de inactividad
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  Clientes marcados como activos sin compras en los últimos 3 años
                </p>
              </div>
              <button onClick={cerrar} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Contenido */}
            <div className="overflow-auto flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-16 text-slate-400">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" /> Analizando...
                </div>
              ) : items.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                  <CheckCheck className="w-10 h-10 mb-3 text-emerald-400" />
                  <p className="font-medium text-slate-600">Sin alertas de inactividad</p>
                  <p className="text-sm mt-1">Todos los activos tienen compras en los últimos 3 años</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b sticky top-0">
                    <tr className="text-left text-slate-500">
                      <th className="px-4 py-3 font-medium">Razón social</th>
                      <th className="px-4 py-3 font-medium">Zona comercial</th>
                      <th className="px-4 py-3 font-medium">Última compra</th>
                      <th className="px-4 py-3 font-medium text-right">Nº docs</th>
                      <th className="px-4 py-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr key={item.id} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="px-4 py-2.5 text-slate-800 font-medium max-w-[280px] truncate" title={item.razon_social}>
                          {item.razon_social}
                        </td>
                        <td className="px-4 py-2.5 text-slate-500 text-xs">
                          {item.zona_comercial || <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge className="bg-amber-100 text-amber-700 text-xs">
                            {item.ultima_compra || '—'}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5 text-right text-slate-500">
                          {item.n_facturas}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-500 hover:text-red-700 hover:border-red-300"
                            disabled={marcando.has(item.id) || marcandoTodas}
                            onClick={() => marcarInactivo(item.id)}
                          >
                            {marcando.has(item.id)
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : 'Marcar inactivo'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pie */}
            {!loading && items.length > 0 && (
              <div className="px-6 py-3 border-t flex items-center justify-between">
                <span className="text-sm text-slate-500">
                  {items.length} entidad{items.length !== 1 ? 'es' : ''} sin actividad reciente
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={cerrar}>Cerrar</Button>
                  <Button
                    className="bg-red-500 hover:bg-red-600 text-white"
                    disabled={marcandoTodas}
                    onClick={marcarTodas}
                  >
                    {marcandoTodas
                      ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Marcando...</>
                      : `Marcar todas inactivas (${items.length})`}
                  </Button>
                </div>
              </div>
            )}
            {!loading && items.length === 0 && (
              <div className="px-6 py-3 border-t flex justify-end">
                <Button variant="outline" onClick={cerrar}>Cerrar</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
